import { useTranslation } from "react-i18next";

import BaseButton from "@/components_v2/Button/BaseButton";
import { BUTTON_VARIANTS } from "@/components_v2/Button/constants";
import { getPathCopy } from "@/domains/certifications/utils";
import { PATH_DECISION_ANCHOR_ID } from "@/domains/certifications/routes";
import type { Track } from "@/redux/certifications/types";
import PathCard from "@/features/certifications/details/components/PathCard";
import { PathsSectionProps } from "@/features/certifications/details/components/types";
import { ENROLLMENT_STATES } from "@/domains/certifications/constants";

import "./styles.css";

const HIGHLIGHTED_TRACK_SLUG = "practice";

const PathsSection = ({
  certification,
  contentKey,
  enrollingTrackId,
  onStart,
  onSwitch,
  onContinue,
}: PathsSectionProps) => {
  const { t } = useTranslation();
  const orderedTracks = [...certification.tracks].sort(
    (a, b) => a.order - b.order,
  );
  const currentTrackId =
    certification.enrollment?.state === ENROLLMENT_STATES.ACTIVE
      ? certification.enrollment.selectedTrack
      : null;

  const renderFooter = (track: Track) => {
    if (currentTrackId === track.id) {
      return (
        <BaseButton
          variant={BUTTON_VARIANTS.PRIMARY}
          isFullWidth
          onClick={onContinue}
        >
          {t("certification.details.continuePath")}
        </BaseButton>
      );
    }

    if (currentTrackId !== null) {
      return (
        <BaseButton
          variant={BUTTON_VARIANTS.PRIMARY}
          isFullWidth
          onClick={() => onSwitch(track)}
        >
          {t("certification.details.switchPath")}
        </BaseButton>
      );
    }

    return (
      <BaseButton
        variant={BUTTON_VARIANTS.PRIMARY}
        isFullWidth
        loading={enrollingTrackId === track.id}
        disabled={enrollingTrackId !== null && enrollingTrackId !== track.id}
        onClick={() => onStart(track.id)}
      >
        {t("certification.details.startPath")}
      </BaseButton>
    );
  };

  return (
    <section
      id={PATH_DECISION_ANCHOR_ID}
      className="certification-paths flex flex-col gap-400"
    >
      <div className="flex flex-col gap-200">
        <h2 className="certification-section-title header-l-medium">
          {t(`${contentKey}.pathsTitle`)}
        </h2>

        <p className="certification-section-description certification-paths-description body-s-regular">
          {t(`${contentKey}.pathsDescription`)}
        </p>
      </div>

      <div className="certification-paths-grid gap-500">
        {orderedTracks.map((track) => (
          <PathCard
            key={track.id}
            copy={getPathCopy(t, contentKey, track)}
            isHighlighted={track.slug === HIGHLIGHTED_TRACK_SLUG}
            footer={renderFooter(track)}
          />
        ))}
      </div>
    </section>
  );
};

export default PathsSection;
